import { ReactNode, useRef } from 'react';

import parse, { Element } from 'html-react-parser';
import { toast } from 'sonner';

import { captureSolveError } from '../lib/error-tracking';
import useAnswerStore from '../stores/answer-store';

interface AnswerSheetProps {
  id: string;
}

const AnswerSheet = ({ id }: AnswerSheetProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { setAnswerMap, answerMap } = useAnswerStore();
  const handleInputChange = (index: number, value: string) => {
    try {
      const updatedInputs = [...answerMap[id].inputs];
      updatedInputs[index] = value;

      const container = document.createElement('div');
      container.innerHTML = answerMap[id].html;
      const fields = container.querySelectorAll('input, select');

      fields.forEach((el, i) => {
        if (el instanceof HTMLSelectElement) {
          el.value = updatedInputs[i] || '';
          el.setAttribute('value', updatedInputs[i] || '');
          el.querySelectorAll('option').forEach(opt => {
            if (opt.value === updatedInputs[i]) {
              opt.setAttribute('selected', 'selected');
            } else {
              opt.removeAttribute('selected');
            }
          });
        } else {
          el.setAttribute('value', updatedInputs[i]);
        }
      });

      setAnswerMap(prev => ({
        ...prev,
        [id]: {
          assignmentProblemId: prev[id].assignmentProblemId,
          html: container.innerHTML,
          inputs: updatedInputs,
        },
      }));
    } catch (err) {
      // 답안 입력 반영 실패 = 학생이 친 답이 저장 안 됨. 반드시 보고.
      captureSolveError(err, 'answer-sheet:input-change', { questionId: id, index });
      toast.error('답안 입력 반영에 실패했습니다. 새로고침 해주세요.');
    }
  };

  let index = 0;

  let parsedHtml: ReactNode = null;
  try {
    parsedHtml = parse(answerMap[id].html, {
    replace: domNode => {
      if (!(domNode instanceof Element)) return;

      const className = domNode.attribs.class || '';
      const inputId = domNode.attribs.id || '';
      if (domNode.name === 'input') {
        const currentIndex = index++;
        return (
          <input
            id={inputId}
            key={`input-${currentIndex}`}
            className={className}
            placeholder="답 입력"
            value={answerMap[id].inputs[currentIndex] || ''}
            onChange={e => handleInputChange(currentIndex, e.target.value)}
          />
        );
      }

      if (domNode.name === 'select') {
        const currentIndex = index++;
        const options = (domNode.children || []).map((child, i) => {
          if (child instanceof Element && child.name === 'option') {
            const value = child.attribs.value || '';

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const label = (child.children?.[0] as any)?.data || value;
            return (
              <option key={i} value={value} selected={value === answerMap[id].inputs[currentIndex]}>
                {label}
              </option>
            );
          }
          return null;
        });

        return (
          <select
            key={`select-${currentIndex}`}
            className={className}
            id={String(currentIndex)}
            value={answerMap[id].inputs[currentIndex] || ''}
            onChange={e => handleInputChange(currentIndex, e.target.value)}
          >
            {options}
          </select>
        );
      }
    },
    });
  } catch (err) {
    // 답안 HTML 파싱 실패 시 컴포넌트 전체가 죽지 않도록 원문이라도 노출하고 보고.
    captureSolveError(err, 'answer-sheet:parse', { questionId: id });
    parsedHtml = answerMap[id].html;
  }

  return (
    <div ref={ref} className="w-full py-2">
      {parsedHtml}
    </div>
  );
};

export default AnswerSheet;

export const parseEditor = (html: string) => {
  return parse(html);
};
